import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, MapPin, Send, Loader2, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('contact_success'));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-[#0A0A0A] mb-4" data-testid="contact-title">
              {t('contact_title')}
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('contact_subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Contact Form */}
            <Card className="rounded-sm border-0 shadow-lg" data-testid="contact-form-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#FF3B30]" />
                  Send us a Message
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('contact_name')}</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-sm"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact_email')}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-sm"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('contact_subject')}</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="How can we help?"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="h-11 rounded-sm"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact_message')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Your message..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="rounded-sm resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold rounded-sm"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('contact_send')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="rounded-sm border-0 shadow-sm bg-[#0A0A0A] text-white">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-[#FF3B30]" />
                      </div>
                      <div>
                        <p className="font-medium">Office</p>
                        <p className="text-gray-400 text-sm">DC LLC<br />Montana, USA</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-sm border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">FAQ</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-[#0A0A0A]">How fast are the alerts?</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Our system monitors TLS Contact 24/7 and sends alerts within minutes of new slots opening.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-[#0A0A0A]">Can I cancel my subscription?</p>
                      <p className="text-gray-600 text-sm mt-1">
                        Yes! You can cancel anytime. Your subscription will remain active until the end of your billing period.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-[#0A0A0A]">Which countries are supported?</p>
                      <p className="text-gray-600 text-sm mt-1">
                        <strong>Residence Countries:</strong> UK, Ireland, Tunisia, Egypt, Morocco, Algeria, Nigeria, Thailand, Iraq, Cameroon, Tanzania, UAE, Qatar, China, India.<br />
                        <strong>Destination Countries:</strong> France, Germany, Spain, Italy, Belgium, Portugal, Malta, Netherlands, Switzerland.<br />
                        <strong>Visa Centers:</strong> TLS Contact, VFS Global, BLS International.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
